'use client'

import { motion } from 'framer-motion'

const particles = [
  {
    top: -20,
    left: 1135,
    h: 2,
    w: 3,
    opacity: 0.56,
    shadow: 'rgba(139,69,255,0.6)',
    delay: 0,
    duration: 7,
    variant: 0,
  },
  {
    top: 208,
    left: 1408,
    h: 3.5,
    w: 2.5,
    opacity: 0.31,
    shadow: 'rgba(139,69,255,0.6)',
    delay: 0.2,
    duration: 6,
    variant: 1,
  },
  {
    top: -13,
    left: 1416,
    h: 1.5,
    w: 3.5,
    opacity: 0.45,
    shadow: 'rgba(0,160,203,0.7)',
    delay: 0.4,
    duration: 8,
    variant: 2,
  },
  {
    top: 303,
    left: 658,
    h: 1.5,
    w: 2.5,
    opacity: 0.44,
    shadow: 'rgba(139,69,255,0.6)',
    delay: 0.6,
    duration: 7.5,
    variant: 0,
  },
  {
    top: 212,
    left: 565,
    h: 1.8,
    w: 2.5,
    opacity: 0.34,
    shadow: 'rgba(255,107,107,0.5)',
    delay: 0.8,
    duration: 6.5,
    variant: 1,
  },
  {
    top: -17,
    left: 1164,
    h: 2,
    w: 2.5,
    opacity: 0.48,
    shadow: 'rgba(255,107,107,0.5)',
    delay: 1,
    duration: 7,
    variant: 2,
  },
  {
    top: 112,
    left: 1356,
    h: 3,
    w: 1.5,
    opacity: 0.54,
    shadow: 'rgba(255,107,107,0.5)',
    delay: 1.2,
    duration: 6,
    variant: 0,
  },
  {
    top: 7.5,
    left: 133,
    h: 1.2,
    w: 2.5,
    opacity: 0.35,
    shadow: 'rgba(255,107,107,0.5)',
    delay: 1.4,
    duration: 8,
    variant: 1,
  },
  {
    top: 65,
    left: 93,
    h: 1.8,
    w: 2.8,
    opacity: 0.47,
    shadow: 'rgba(139,69,255,0.6)',
    delay: 1.6,
    duration: 7.5,
    variant: 2,
  },
  {
    top: 265,
    left: 1203,
    h: 2.5,
    w: 3.5,
    opacity: 0.31,
    shadow: 'rgba(0,160,203,0.7)',
    delay: 1.8,
    duration: 6.5,
    variant: 0,
  },
  {
    top: 313,
    left: 48,
    h: 1.2,
    w: 3.5,
    opacity: 0.38,
    shadow: 'rgba(139,69,255,0.6)',
    delay: 2,
    duration: 7,
    variant: 1,
  },
  {
    top: 59,
    left: 573,
    h: 2.8,
    w: 3,
    opacity: 0.34,
    shadow: 'rgba(139,69,255,0.6)',
    delay: 2.2,
    duration: 8,
    variant: 2,
  },
  {
    top: 108,
    left: 79,
    h: 3.5,
    w: 2.5,
    opacity: 0.33,
    shadow: 'rgba(139,69,255,0.6)',
    delay: 2.4,
    duration: 6,
    variant: 0,
  },
  {
    top: 118,
    left: 1063,
    h: 2.5,
    w: 2.5,
    opacity: 0.33,
    shadow: 'rgba(255,107,107,0.5)',
    delay: 2.6,
    duration: 7.5,
    variant: 1,
  },
  {
    top: 38,
    left: 1194,
    h: 2.8,
    w: 4,
    opacity: 0.58,
    shadow: 'rgba(255,255,255,0.4)',
    delay: 2.8,
    duration: 6.5,
    variant: 2,
  },
]

interface FloatingParticleProps {
  top: number
  left: number
  h: number
  w: number
  opacity: number
  shadow: string
  delay: number
  duration: number
  variant: number
}

function FloatingParticle({
  top,
  left,
  h,
  w,
  opacity,
  shadow,
  delay,
  duration,
  variant,
}: FloatingParticleProps) {
  const size = Math.max(h, w)

  const variants = [
    {
      yDistance: 15 + size * 1.5,
      xDistance: 3 + size * 0.3,
      scaleRange: [1, 1.08, 1],
      opacityMultiplier: 0.85,
    },
    {
      yDistance: 10 + size * 1,
      xDistance: 6 + size * 0.6,
      scaleRange: [1, 1.12, 1],
      opacityMultiplier: 0.75,
    },
    {
      yDistance: 12 + size * 1.2,
      xDistance: 4 + size * 0.4,
      scaleRange: [1, 1.15, 1],
      opacityMultiplier: 0.8,
    },
  ]

  const animationVariant = variants[variant % 3]

  return (
    <motion.div
      className="absolute rounded-full bg-white blur-[0.25px]"
      style={{
        top: `${top}px`,
        left: `${left}px`,
        height: `${size}px`,
        width: `${size}px`,
        opacity,
        boxShadow: `0px 0px ${size * 2}px ${shadow}`,
      }}
      animate={{
        y: [0, -animationVariant.yDistance, 0],
        x: [-animationVariant.xDistance, animationVariant.xDistance, -animationVariant.xDistance],
        scale: animationVariant.scaleRange,
        opacity: [opacity, opacity * animationVariant.opacityMultiplier, opacity],
      }}
      transition={{
        duration,
        repeat: Infinity,
        ease: 'easeInOut',
        delay,
      }}
    />
  )
}

export default function FloatingParticlesBackground() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {particles.map((particle, index) => (
        <FloatingParticle key={index} {...particle} />
      ))}
    </div>
  )
}
